<template>
  <div :id="id || undefined">
    <transition :name="transitionName">
      <div class="modal-mask" v-if="showModal" @click="bgClicked($event)" :style="{ 'z-index': zIndexFinal }">
        <div class="modal-box" :style="{ 'background-color': bgColor }" :class="{ 'trans': bgColor === 'transparent', [size]: true, 'centered': centered }" @click="boxClicked($event)">
          <div class="content">
            <div class="modal-header" v-if="!hideHeader">
              <slot name="header">
                <span class="modal-title">{{ title }}</span>
              </slot>
              <button v-if="!hideHeaderClose" class="modal-close-x" @click="close">&times;</button>
            </div>

            <div class="modal-body" :class="{'no-scroll': noScroll}">
              <slot></slot>
            </div>

            <div class="app-modal-footer no-print" v-if="showFooter">
              <slot name="footer">
                <button class="btn btn-sm close-btn" @click="close">
                  Close
                </button>
                <button v-if="showOk" class="btn btn-sm ok-btn" @click="ok">
                  OK
                </button>
              </slot>
            </div>

          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// VERSION 1.5
const openModalStack = [];

export default {
  data: function () {
    return {
      showModal: false,
      zIndexFinal: 1100
    }
  },
  mounted() {
    // Move to body so position:fixed is always relative to the viewport,
    // not a transformed ancestor (CSS transforms break fixed positioning).
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    this._unregister();
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  props: {
    "id": {
      type: String,
      default: ""
    },
    "bgColor": String,
    "title": String,
    "size": {
      type: String,
      default: "md"
    },
    "showFooter": {
      type: Boolean,
      default: false
    },
    "hideHeader": {
      type: Boolean,
      default: true
    },
    "showOk": {
      type: Boolean,
      default: false
    },
    "noScroll": {
      type: Boolean,
      default: false
    },
    "isShown": {
      type: Boolean,
      default: null
    },
    "zIndex": {
      type: Number,
      default: null
    },
    "centered": {
      type: Boolean,
      default: false
    },
    "animation": {
      type: String,
      default: "fade"  // "fade" | "pop"
    },
    "hideHeaderClose": {
      type: Boolean,
      default: false
    },
    "noBackdropClose": {
      type: Boolean,
      default: false
    }
  },
  computed: {
    transitionName() {
      return this.animation === 'fade' ? 'modal' : 'modal-' + this.animation;
    }
  },
  watch:{
    isShown(){
      if(this.isShown === true)
        this.show();
      else
        this.hide();
    }
  },
  methods: {
    boxClicked(e) {
      // Don't stopPropagation — Bootstrap JS needs events to bubble for data-toggle
      //e.stopPropagation();
    },
    bgClicked(event) {
      // check if no other elements were clicked before it and modal-box transition completed
      if (!this.noBackdropClose && event.target.classList.contains("modal-mask") && !event.target.classList.contains("modal-enter-active")) {
        event.stopPropagation();
        this.close();
      }
    },
    show() {
        if(this.zIndex !== null)
            this.zIndexFinal = this.zIndex;
        else if (window.zIndex) {
            this.zIndexFinal = window.zIndex + 1;
            window.zIndex = this.zIndexFinal;
        }
        else {
            window.zIndex = this.zIndexFinal;
        }

        this.showModal = true;
        if (!openModalStack.includes(this)) {
            openModalStack.push(this);
            document.addEventListener('keydown', this._onKeyDown);
        }

        this.$emit("onShown");
    },
    // Whenever we r closing this window
    hide() {
      if (!this.showModal)
        return;
      this.showModal = false;
      this._unregister();
      this.$emit("onHide");
    },
    _unregister() {
      const idx = openModalStack.indexOf(this);
      if (idx !== -1) openModalStack.splice(idx, 1);
      document.removeEventListener('keydown', this._onKeyDown);
    },
    _onKeyDown(e) {
      if (e.key === 'Escape' && openModalStack[openModalStack.length - 1] === this) {
        this.close();
      }
    },
    // Only from within this window when we chose to close
    close() {
      this.$emit('close');
      if(this.isShown === null)
        this.hide();
    },
    ok() {
      this.$emit('ok');    
    }
  }
}
</script>

<style scoped>
.app-modal-footer {
  padding: 8px 0px 0px 0px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.close-btn {
  border: none;
  border-radius: 6px;
  color: #555;
  background-color: #e9ecef;
  padding: 4px 16px;
  font-size: 0.85rem;
  transition: background-color 0.15s ease;
}

.close-btn:hover {
  background-color: #d3d7db;
}

.ok-btn {
  border: none;
  border-radius: 6px;
  color: #fff;
  background-color: #129357;
  padding: 4px 16px;
  font-size: 0.85rem;
  transition: background-color 0.15s ease;
}

.ok-btn:hover {
  background-color: #0e7a48;
}

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease;
  display: flex;
  overflow: hidden;
}

.modal-box {
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: 800px;
  min-height: 500px;
  margin: 40px auto auto;
  background: white;
  position: relative;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  max-height: calc(100vh - 60px);
  max-height: calc(100dvh - 60px);
}

.modal-box.sm {
  max-width: 600px;
  width: fit-content;
  min-height: unset;
  margin-top: 60px;
  max-height: calc(100vh - 80px);
  max-height: calc(100dvh - 80px);
}

.modal-box.md {
  max-width: 800px;
}

.modal-box.lg {
  max-width: 1050px;
  margin-top: 20px;
  max-height: calc(100vh - 40px);
  max-height: calc(100dvh - 40px);
}

.modal-box.xl {
  max-width: 1200px;
  margin-top: 15px;
  max-height: calc(100vh - 35px);
  max-height: calc(100dvh - 35px);
}

.modal-box.full {
  width: 100%;
  margin-top: 10px;
  max-height: calc(100vh - 20px);
  max-height: calc(100dvh - 20px);
}

.modal-box.centered {
  margin: auto;
  max-height: calc(100vh - 40px);
  max-height: calc(100dvh - 40px);
}

.modal-box .body {
  margin: 0px 0;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 10px;
}

.trans {
  box-shadow: 0 0px 0px !important;
  border: 0 !important;
  background: transparent !important;
  /*min-width: fit-content;
  max-width: fit-content;*/
}

.modal-header {
  margin-top: 0;
  padding: 10px 12px;
  font-size: 1rem;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.modal-title {
  font-weight: 600;
  font-size: 1.05rem;
}

.modal-close-x {
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #999;
  cursor: pointer;
  padding: 0 4px;
}

.modal-close-x:hover {
  color: #333;
}

:deep(.modal-header h1) {
  text-align: center;
  margin-top: 10px;
  color: wheat;
}

.modal-header h3 {
  margin-top: 0;
  color: #42b983;
}

.modal-default-button {
  float: right;
}

/*
  * The following styles are auto-applied to elements with
  * transition="modal" when their visibility is toggled
  * by Vue.js.
  *
  * You can easily play with the modal transition by editing
  * these styles.
  */

.modal-enter {
  opacity: 0;
}

/* ── Pop animation (awards, rewards, celebrations) ── */
.modal-pop-enter, .modal-pop-leave-to {
  opacity: 0;
}
.modal-pop-enter-active,
.modal-pop-leave-active {
  transition: opacity 0.3s ease;
}
.modal-pop-enter-active .modal-box {
  animation: modal-pop-in 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
}
.modal-pop-leave-active .modal-box {
  animation: modal-pop-out 0.2s ease-in both;
}

@keyframes modal-pop-in {
  from {
    opacity: 0;
    transform: scale(0.55) translateY(-30px);
  }
  65% {
    transform: scale(1.06) translateY(0);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes modal-pop-out {
  to {
    opacity: 0;
    transform: scale(0.8);
  }
}

.modal-body {
  flex-grow: 1;
  width: 100%;
  color: black;
  overflow: auto;
}

.no-scroll {
  overflow: inherit;
}

:deep(.light-text) {
  color: wheat;
}

:deep(.light-text h1) {
  text-align: center;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

@media screen and (max-height: 650px) {
  .modal-box {
    min-height: unset;
    margin-top: 5px !important;
    max-height: calc(100vh - 10px) !important;
    max-height: calc(100dvh - 10px) !important;
  }
}

@media screen and (max-width: 700px) {
  .modal-box {
    width: calc(100vw - 20px) !important;
    min-height: unset;
    margin-top: 20px;
    max-height: calc(100vh - 30px);
    max-height: calc(100dvh - 30px);
  }
}
</style>
